/**
 * Script-specific webfont loading
 * @module @infra/i18n
 */

import type { Language } from './i18n.types';

/**
 * Webfont each language needs beyond the app's Latin faces.
 *
 * Only scripts the bundled fonts cannot render belong here. Khmer and Myanmar are not
 * optional polish — the Latin faces have no glyphs for either script, so without these
 * the screen renders tofu boxes. The Han family is an order of magnitude larger than a
 * Latin one, so all three are fetched on demand rather than from `index.html`;
 * languages that never show these scripts never pay for them.
 */
const WEBFONT_BY_LANGUAGE: Partial<Record<Language, { id: string; href: string }>> = {
  zh: {
    id: 'webfont-noto-sans-hk',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+HK:wght@300;400;500;600;700&display=swap',
  },
  km: {
    id: 'webfont-noto-sans-khmer',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+Khmer:wght@300;400;500;600;700&display=swap',
  },
  my: {
    id: 'webfont-noto-sans-myanmar',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+Myanmar:wght@300;400;500;600;700&display=swap',
  },
};

/**
 * Ensure the webfont for `language` is in the document. Idempotent — repeated calls
 * (language switches, re-init) reuse the existing tag.
 */
export function ensureLanguageWebfont(language: Language): void {
  if (typeof document === 'undefined') return;

  const font = WEBFONT_BY_LANGUAGE[language];
  if (!font || document.getElementById(font.id)) return;

  const link = document.createElement('link');
  link.id = font.id;
  link.rel = 'stylesheet';
  link.href = font.href;
  document.head.appendChild(link);
}
